/**
 * assets/kore-banner-video.js
 *
 * Desktop/mobile video switching for sections/image-banner.liquid's
 * .js-ib-video element. There is only ever ONE <video> in the DOM — this
 * script decides, via a single matchMedia('(max-width: 749px)') listener
 * (event-driven, no resize polling, no requestAnimationFrame), which
 * breakpoint's source/poster belong on it, and only ever attaches that one
 * <source>. The non-matching breakpoint's file is never requested, so
 * desktop and mobile never download or play at the same time.
 *
 * Desktop/mobile src, type and poster travel in on data-* attributes (set by
 * Liquid, which already picked the highest-resolution MP4 for each — see
 * image-banner.liquid). If a breakpoint has no source of its own, its
 * data-*-src is simply absent and apply() no-ops for that side (Liquid
 * already resolved the "mobile falls back to desktop" case before this runs,
 * so in practice both sides normally have a value whenever any video is set).
 *
 * Recovery: global.js's pauseAllMedia() calls .pause() on every <video> on
 * the page (opening/closing any modal-dialog — e.g. a quick-add card, or
 * clicking a deferred-media poster anywhere on the page — triggers it),
 * with no notion that this one is a decorative, always-on background loop.
 * The same "paused and nothing ever resumes it" gap also covers WebKit's own
 * media-resource suspension after a long backgrounded/inactive tab and
 * bfcache restores. Three event-driven listeners below cover all of it —
 * no polling, no stall/waiting watchdog (nothing in this audit points to a
 * network/stall problem, so none was added).
 */
(function () {
  function resume(video) {
    if (!video.dataset.currentSrc) return; // nothing loaded yet — apply() hasn't run
    if (document.visibilityState !== 'visible') return; // pointless while hidden; visibilitychange below covers the return
    video.muted = true;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        /* Still blocked for some reason — fail silently, try again on the next trigger. */
      });
    }
  }

  function apply(video, isMobile) {
    var src = isMobile ? video.dataset.mobileSrc : video.dataset.desktopSrc;
    var type = isMobile ? video.dataset.mobileType : video.dataset.desktopType;
    var poster = isMobile ? video.dataset.mobilePoster : video.dataset.desktopPoster;

    if (!src) return; // nothing to show for this breakpoint
    if (video.dataset.currentSrc === src) return; // already showing this exact file — no-op

    // "A swap is in flight" lives for exactly as long as this play() promise is
    // pending — cleared when it settles (resolve OR reject), never by waiting
    // for a 'pause' event. video.pause() below is a no-op with NO event fired
    // if the video happens to already be paused (e.g. an external pause landed
    // moments earlier) — tying the flag's lifetime to that event would leave it
    // stuck "true" forever in exactly that case, silently swallowing every
    // external pause after it. A promise, unlike an event, always settles.
    video._koreSwitching = true;

    video.pause();
    video.dataset.currentSrc = src;
    video.muted = true;
    video.innerHTML = '';

    var source = document.createElement('source');
    source.src = src;
    if (type) source.type = type;
    video.appendChild(source);

    if (poster) video.setAttribute('poster', poster);

    video.load();
    var playPromise = video.play();
    var clearSwitching = function () {
      video._koreSwitching = false;
    };
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.then(clearSwitching, clearSwitching); // settles either way — autoplay-blocked included
    } else {
      clearSwitching(); // older browsers: play() returned no promise, nothing async to wait on
    }
  }

  function attachRecovery(video) {
    // Something outside this file paused it (pauseAllMedia(), WebKit resource
    // management, etc.) — resume, unless it's our own deliberate pause during
    // a breakpoint source swap (see apply()), or the tab is hidden (in which
    // case visibilitychange below handles the eventual return).
    function onPause() {
      if (video._koreSwitching) return;
      resume(video);
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible' && video.paused) resume(video);
    }
    // Safari bfcache restore: media playback isn't reliably resumed on its own.
    function onPageShow(event) {
      if (event.persisted && video.paused) resume(video);
    }

    video.addEventListener('pause', onPause);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);

    // Theme editor only: shopify:section:load builds a brand new <video> on
    // every reload, but the visibilitychange/pageshow listeners above are on
    // document/window — those never go away on their own, and their closures
    // would keep pinning this (now-removed) video element in memory, one
    // stale set per reload. Cleaned up on shopify:section:unload. Production
    // never fires this event (the section is never reloaded in place), so
    // this whole block never runs there — one video, one set of listeners,
    // for the page's lifetime.
    if (window.Shopify && window.Shopify.designMode) {
      var section = video.closest('.shopify-section');
      if (section) {
        var onUnload = function (event) {
          if (event.target !== section) return;
          document.removeEventListener('visibilitychange', onVisibilityChange);
          window.removeEventListener('pageshow', onPageShow);
          document.removeEventListener('shopify:section:unload', onUnload);
        };
        document.addEventListener('shopify:section:unload', onUnload);
      }
    }
  }

  function setup(video) {
    if (!video || video.dataset.ibInit) return;
    video.dataset.ibInit = 'true';

    var mq = window.matchMedia('(max-width: 749px)');
    apply(video, mq.matches);
    mq.addEventListener('change', function (event) {
      apply(video, event.matches);
    });

    attachRecovery(video);
  }

  function init(root) {
    (root || document).querySelectorAll('.js-ib-video').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-run when this section is added/reloaded without a full page navigation.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
